import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { modeTranslations } from '../modes';

const translations = {
    en: {
        languageSwitcher: {
            ariaLabel: 'Language selection',
            options: {
                en: {
                    short: 'EN',
                    label: 'English',
                    title: 'Switch to English',
                },
                sk: {
                    short: 'SK',
                    label: 'Slovak',
                    title: 'Switch to Slovak',
                },
            },
        },
        common: {
            close: 'Close',
        },
        controls: {
            title: 'Controls',
            dragTooltip: 'Drag to reposition controls',
            dragSrLabel: 'Drag',
            generation: 'Generation',
            step: 'Step',
            start: 'Start',
            stop: 'Stop',
            clear: 'Clear',
            speed: 'Speed',
            speedUnits: 'ms',
            speedTooltip: 'Period (milliseconds) between generations. Lower values run faster.',
            testPeriodicity: 'Test Periodicity',
            stopTesting: 'Stop Testing',
            testingPeriodicity: 'Testing for periodicity...',
            period: 'Period',
        },
        grid: {
            libraryButton: 'Library',
            libraryButtonTitle: 'Patterns & configurations',
            title: 'Game of Life',
            activeMode: 'Mode: {{mode}}',
            modesButton: 'Modes',
            modesButtonTitle: 'Modes panel',
            helpButtonTitle: 'How to use',
            patternSearchButton: 'Search',
            patternSearchButtonTitle: 'Pattern Search',
        },
        menu: {
            headingEyebrow: 'Library',
            headingTitle: 'Patterns & Configurations',
            tabs: {
                patterns: 'Patterns',
                configurations: 'Configurations',
            },
            patternsSectionTitle: 'Patterns:',
            configurationsSectionTitle: 'Configurations:',
            customSectionLabel: 'Custom',
            tooltips: {
                close: 'Close',
                savePattern: 'Save pattern to local storage',
                copyRle: 'Copy current pattern RLE to clipboard',
                dragPattern: 'Drag to place on grid or click to load at origin',
                downloadCurrentPattern: 'Download current grid as JSON pattern',
                remove: 'Remove',
                rename: 'Rename',
                saveConfiguration: 'Save configuration to local storage',
                loadConfiguration: 'Load configuration from file',
                loadConfigurationButton: 'Click to load full grid',
            },
            prompts: {
                removeConfirm: 'Remove "{{name}}"?',
                renamePrompt: 'Enter a new name',
                nameExists: 'That name is already in use.',
            },
            messages: {
                noLiveCells: 'No live cells to save.',
                copiedToClipboard: 'Copied to clipboard!',
            },
            generatedNames: {
                pattern: 'Pattern {{day}}.{{month}}.{{year}} {{hours}}:{{minutes}}',
                configuration: 'Config {{day}}.{{month}}.{{year}} {{hours}}:{{minutes}}',
                patternDescription: 'Pattern saved at {{timestamp}}',
                configurationDescription: 'Configuration saved at {{timestamp}}',
            },
            descriptionLabel: 'Description',
            descriptionPlaceholder: 'Hover over a pattern to see details...',
        },
        help: {
            title: 'How to use the game',
            subtitle:
                'Master the controls, explore patterns, and keep the simulation flowing - everything you need for fast iteration lives here.',
            close: 'Close',
            shortcutsTitle: 'Shortcuts & gestures',
            languageHeading: 'Language',
            languageDescription: 'Switch the interface language instantly. Preferences are saved automatically.',
            sections: {
                simulationBasics: {
                    title: 'Simulation basics',
                    bullets: {
                        runPause: {
                            label: 'Run & pause',
                            body: 'Use the Start/Stop button in the floating controls to toggle the simulation without clearing the board.',
                        },
                        singleStep: {
                            label: 'Toggle cell state',
                            body: 'Press Step to advance exactly one generation - perfect for debugging patterns or inspecting fuzzy transitions.',
                        },
                        clearGrid: {
                            label: 'Clear selected region',
                            body: 'Reset the world instantly with Clear. This keeps the current mode, speed, and camera position intact.',
                        },
                    },
                },
                libraryFiles: {
                    title: 'Library & Files',
                    bullets: {
                        patternsTab: {
                            label: 'Patterns tab',
                            body: 'Drag any pattern onto the grid to drop it where you like. Saved patterns are normalized to their top-left cell.',
                        },
                        configurationsTab: {
                            label: 'Configurations tab',
                            body: 'Click a configuration to replace the entire board. Use the upload icon to import `.rle` exports.',
                        },
                        localStorage: {
                            label: 'Local storage',
                            body: 'Custom saves live in your browser. Clearing site data removes them, so export important work regularly.',
                        },
                        copyRle: {
                            label: 'Copy RLE',
                            body: 'Quickly export any pattern or configuration by copying its RLE code directly to your clipboard.',
                        },
                    },
                },
                advancedFeatures: {
                    title: 'Advanced Features',
                    bullets: {
                        patternSearcher: {
                            label: 'Pattern Searcher',
                            body: 'Automatically discover new gliders and oscillators in 1D modes. Find it in the Modes panel.',
                        },
                        weightEditor: {
                            label: 'Weight Editor',
                            body: 'Customize rules and neighborhood weights for 1D automata to explore unique behaviors.',
                        },
                        patternPresets: {
                            label: 'Pattern Presets',
                            body: 'Quickly apply known interesting rule combinations and associated patterns from the Modes panel.',
                        },
                    },
                },
            },
        },
        hotkeys: {
            keyLabels: {
                click: 'Click',
                drag: 'Drag',
                alt: 'Alt',
                scroll: 'Scroll',
                zero: '0',
                dragPatternCard: 'Drag pattern card',
                shift: 'Shift',
                ctrl: 'Ctrl',
                z: 'Z',
            },
            sections: {
                navigation: {
                    title: 'Navigation',
                    shortcuts: {
                        panCamera: {
                            action: 'Pan camera',
                            description: 'Move around large boards while keeping your zoom level intact.',
                        },
                        zoomAtCursor: {
                            action: 'Zoom at cursor',
                            description: 'Smoothly zoom in or out, anchored to wherever your pointer is hovering.',
                        },
                        resetZoom: {
                            action: 'Reset zoom',
                            description: 'Return to the default zoom level and recenter the grid in one motion.',
                        },
                    },
                },
                editing: {
                    title: 'Editing',
                    shortcuts: {
                        toggleCell: {
                            action: 'Toggle Cell',
                            description:
                                'Activate or deactivate a single cell. In fuzzy modes, toggles jump between off and a high starting value.',
                        },
                        dropPattern: {
                            label: 'Drop pattern',
                            description:
                                'Drag from the Library to preview placement, then release to stamp it into the world.',
                        },
                        clearSelection: {
                            action: 'Clear Selection',
                            description:
                                'Hold Shift and drag to outline a rectangle, then release to wipe every cell inside it.',
                        },
                        undoAction: {
                            label: 'Undo last action',
                            description:
                                'Press Ctrl+Z to revert the most recent edit, whether it was a clear, toggle, or pattern drop.',
                        },
                    },
                },
            },
        },
        modeMenu: {
            title: 'Modes',
            subtitle: 'Switch behaviours and fine-tune how the simulation evolves.',
            closeTitle: 'Close modes panel',
            rules: 'Rules',
            weights: 'Weights',
            parameters: 'Parameters',
            instantUpdate: 'Values update instantly',
            boolean: {
                on: 'On',
                off: 'Off',
            },
            noParameters: 'This mode uses default constants and does not expose any adjustable parameters yet.',
            emptyState: 'Select a mode from the list to view its details.',
            '1dInfo': {
                title: 'Advanced Configuration',
                weights: 'Use the Weights button above to configure neighborhood weights, size, and threshold settings.',
            },
            configureWeights: 'Configure weights',
            '1dWeightsHelp': 'Configure neighborhood weights, size, threshold, and rules for 1D cellular automaton.',
            patternSearch: 'Pattern Search',
            '1dSearchHelp': 'Automatically search for oscillators and gliders in 1D cellular automaton.',
            patternPresets: 'PATTERN PRESETS',
            patternPresetsHelp: 'Rule settings with discovered patterns. Click to apply.',
            patternSingular: 'pattern',
            patternPlural: 'patterns',
        },
        modeRules: {
            title: '{{mode}} Rules',
            unknownTitle: 'Unknown mode rules',
            fallback:
                '<div class="rounded-lg border border-slate-700/70 bg-slate-800/60 p-4 text-slate-200">No rules available.</div>',
            close: 'Close',
        },
        info: {
            generation: 'Generation:',
        },
        alerts: {
            invalidConfigurationFile: 'Invalid configuration file.',
        },
        weightEditor: {
            title: 'Weight Editor',
            subtitle: 'Click on cells to set their weights',
            center: 'Center',
            legend: 'Weight Intensity',
            neighborhoodSize: 'Neighborhood Size',
            neighborhoodSizeHelp: 'Neighbors on each side',
            symmetric: 'Symmetric Weights',
            symmetricHelp: 'Keep left and right weights equal (mirror symmetry)',
            birthRules: 'Birth Rules',
            birthRulesHelp: 'Allowed weighted sums for birth (e.g. 2, 3 or 2.5-4)',
            survivalRules: 'Survival Rules',
            survivalRulesHelp: 'Allowed weighted sums for survival (e.g. 2, 4 or 2-4.5)',
            currentSum: 'Current weighted sum depends on active cells',
            howItWorks: 'How it works',
            info1: 'Each neighbor cell contributes its weight when alive',
            info2: 'The center cell (X) is the cell being evaluated',
            info3: 'Birth: dead cell becomes alive if weighted sum matches rules (range or set)',
            info4: 'Survival: live cell stays alive if weighted sum matches rules (range or set)',
            info5: 'Neighborhood size determines how many neighbors on each side',
            reset: 'Reset to Default',
            cancel: 'Cancel',
            apply: 'Apply',
        },
        patternSearch: {
            title: 'Pattern Searcher',
            config: {
                patternWidth: 'Pattern Width',
                searchWidth: 'Search Width',
                maxGen: 'Max Gen',
                minSeedDensity: 'Min Density',
                maxSeedDensity: 'Max Density',
                strategy: 'Strategy',
                searchSmaller: 'Search smaller widths',
                strategies: {
                    random: 'Random',
                    bruteForce: 'Brute Force',
                },
                startSearch: 'Start Search',
                stopSearch: 'Stop Search',
                clearResults: 'Clear Results',
                exportResults: 'Export to JSON',
            },
            filters: {
                title: 'Filters',
                type: 'Type',
                all: 'All',
                gliders: 'Gliders',
                oscillators: 'Oscillators',
                stillLifes: 'Still Life',
                minP: 'Min P',
                maxP: 'Max P',
                shift: 'Shift',
            },
            progress: {
                trials: 'Trials',
                found: 'Found',
                searching: 'Searching...',
            },
            results: {
                noPatternsFound: 'No patterns found matching filters.',
                noPatternsYet: 'No patterns found yet. Adjust width or generations and start searching.',
                load: 'Load',
            },
            info: {
                searchFooter: 'Searcher checks for repeating trimmed bitstrings across generations. Gliders are detected by spatial shift between repetitions.',
            }
        },
    },
    sk: {
        languageSwitcher: {
            ariaLabel: 'Voľba jazyka',
            options: {
                en: {
                    short: 'EN',
                    label: 'Angličtina',
                    title: 'Prepnúť do angličtiny',
                },
                sk: {
                    short: 'SK',
                    label: 'Slovenčina',
                    title: 'Prepnúť do slovenčiny',
                },
            },
        },
        common: {
            close: 'Zavrieť',
        },
        controls: {
            title: 'Ovládanie',
            dragTooltip: 'Potiahnite pre premiestnenie ovládacieho panelu',
            dragSrLabel: 'Presunúť',
            generation: 'Generácia',
            step: 'Krok',
            start: 'Spustiť',
            stop: 'Zastaviť',
            clear: 'Vyčistiť',
            speed: 'Rýchlosť',
            speedUnits: 'ms',
            speedTooltip: 'Interval (v milisekundách) medzi generáciami. Nižšie hodnoty znamenajú rýchlejší beh.',
            testPeriodicity: 'Testovať periodicitu',
            stopTesting: 'Zastaviť testovanie',
            testingPeriodicity: 'Testovanie periodicity...',
            period: 'Perióda',
        },
        grid: {
            libraryButton: 'Knižnica',
            libraryButtonTitle: 'Vzory a konfigurácie',
            title: 'Hra života',
            activeMode: 'Režim: {{mode}}',
            modesButton: 'Režimy',
            modesButtonTitle: 'Panel režimov',
            helpButtonTitle: 'Ako používať',
            patternSearchButton: 'Hľadať',
            patternSearchButtonTitle: 'Hľadanie vzorov',
        },
        menu: {
            headingEyebrow: 'Knižnica',
            headingTitle: 'Vzory a konfigurácie',
            tabs: {
                patterns: 'Vzory',
                configurations: 'Konfigurácie',
            },
            patternsSectionTitle: 'Vzory:',
            configurationsSectionTitle: 'Konfigurácie:',
            customSectionLabel: 'Vlastné',
            tooltips: {
                close: 'Zavrieť',
                savePattern: 'Uložiť vzor do lokálneho úložiska',
                copyRle: 'Skopírovať RLE kód aktuálneho vzoru do schránky',
                dragPattern: 'Potiahnite pre umiestnenie na mriežku alebo kliknite pre načítanie na začiatok',
                remove: 'Odstrániť',
                rename: 'Premenovať',
                saveConfiguration: 'Uložiť konfiguráciu do lokálneho úložiska',
                loadConfiguration: 'Načítať konfiguráciu zo súboru',
                loadConfigurationButton: 'Kliknite pre načítanie celej mriežky',
            },
            prompts: {
                removeConfirm: 'Odstrániť "{{name}}"?',
                renamePrompt: 'Zadajte nový názov',
                nameExists: 'Tento názov sa už používa.',
            },
            messages: {
                noLiveCells: 'Nie sú žiadne živé bunky na uloženie.',
                copiedToClipboard: 'Skopírované do schránky!',
            },
            generatedNames: {
                pattern: 'Vzor {{day}}.{{month}}.{{year}} {{hours}}:{{minutes}}',
                configuration: 'Konfigurácia {{day}}.{{month}}.{{year}} {{hours}}:{{minutes}}',
                patternDescription: 'Vzor uložený {{timestamp}}',
                configurationDescription: 'Konfigurácia uložená {{timestamp}}',
            },
            descriptionLabel: 'Popis',
            descriptionPlaceholder: 'Umiestnite kurzor nad vzor pre zobrazenie podrobností...',
        },
        help: {
            title: 'Používanie aplikácie',
            subtitle:
                'Získajte prehľad o ovládaní, preskúmajte dostupné vzory a efektívne pracujte so simuláciou. Na tomto mieste nájdete všetky základné informácie pre rýchlu orientáciu.',
            close: 'Zavrieť',
            shortcutsTitle: 'Klávesové skratky a gestá',
            languageHeading: 'Jazyk',
            languageDescription: 'Zmena jazyka rozhrania sa aplikuje okamžite a automaticky sa uloží.',
            sections: {
                simulationBasics: {
                    title: 'Základy simulácie',
                    bullets: {
                        runPause: {
                            label: 'Spustenie a pozastavenie',
                            body: 'Pomocou tlačidla Štart/Stop v ovládacom paneli môžete prepínať beh simulácie bez ovplyvnenia aktuálneho stavu mriežky.',
                        },
                        singleStep: {
                            label: 'Jednokrokový režim',
                            body: 'Funkcia Krok umožňuje posun o jednu generáciu, čo je vhodné najmä pri analýze správania vzorov alebo sledovaní prechodových javov.',
                        },
                        clearGrid: {
                            label: 'Vymazanie mriežky',
                            body: 'Voľbou Vyčistiť odstránite aktuálnu konfiguráciu bez zmeny zvoleného režimu, rýchlosti simulácie ani nastavenia zobrazenia.',
                        },
                    },
                },
                libraryFiles: {
                    title: 'Knižnica a súbory',
                    bullets: {
                        patternsTab: {
                            label: 'Karta vzorov',
                            body: 'Vybraný vzor je možné presunúť na mriežku metódou „drag and drop“. Vzory sú pri ukladaní normalizované vzhľadom na svoju ľavú hornú bunku.',
                        },
                        configurationsTab: {
                            label: 'Karta konfigurácií',
                            body: 'Výber konfigurácie nahradí aktuálny stav celej mriežky. Import súborov vo formáte `.rle` je dostupný prostredníctvom príslušnej ikony.',
                        },
                        localStorage: {
                            label: 'Lokálne úložisko',
                            body: 'Používateľské dáta sú ukladané lokálne v prehliadači. Pri vymazaní údajov stránky dôjde k ich odstráneniu, preto sa odporúča dôležité konfigurácie pravidelne exportovať.',
                        },
                        copyRle: {
                            label: 'Export RLE',
                            body: 'Vzor alebo konfiguráciu je možné exportovať skopírovaním ich RLE reprezentácie priamo do systémovej schránky.',
                        },
                    },
                },
                advancedFeatures: {
                    title: 'Pokročilé funkcie',
                    bullets: {
                        patternSearcher: {
                            label: 'Vyhľadávač vzorov',
                            body: 'Nástroj umožňuje automatizované vyhľadávanie zaujímavých štruktúr, ako sú klzáky alebo oscilátory, najmä v jednorozmerných režimoch.',
                        },
                        weightEditor: {
                            label: 'Editor váh',
                            body: 'Umožňuje detailnú konfiguráciu váh susedstva a pravidiel pre jednorozmerné automaty, čím otvára priestor pre experimentovanie s rôznymi dynamikami.',
                        },
                        patternPresets: {
                            label: 'Predvoľby vzorov',
                            body: 'Preddefinované kombinácie pravidiel a vzorov umožňujú rýchle nastavenie simulácie bez nutnosti manuálnej konfigurácie.',
                        },
                    },
                },
            },
        },
        hotkeys: {
            keyLabels: {
                click: 'Klik',
                drag: 'Ťah',
                alt: 'Alt',
                scroll: 'Rolovanie',
                zero: '0',
                dragPatternCard: 'Potiahnuť kartu vzoru',
                shift: 'Shift',
                ctrl: 'Ctrl',
                z: 'Z',
            },
            sections: {
                navigation: {
                    title: 'Navigácia',
                    shortcuts: {
                        panCamera: {
                            action: 'Posúvanie kamery',
                            description: 'Pohybujte sa po veľkých mriežkach bez zmeny priblíženia.',
                        },
                        zoomAtCursor: {
                            action: 'Priblíženie pri kurzore',
                            description: 'Plynule približujte alebo odďaľujte so zachovaním pozície kurzora.',
                        },
                        resetZoom: {
                            action: 'Obnoviť priblíženie',
                            description: 'Jedným krokom sa vráťte na predvolené priblíženie a vycentrujte mriežku.',
                        },
                    },
                },
                editing: {
                    title: 'Úpravy',
                    shortcuts: {
                        toggleCell: {
                            action: 'Prepnutie bunky',
                            description:
                                'Aktivujte alebo deaktivujte jednotlivú bunku. Vo fuzzy režimoch prepína medzi vypnutím a vysokou počiatočnou hodnotou.',
                        },
                        dropPattern: {
                            action: 'Umiestniť vzor',
                            description:
                                'Pretiahnite z knižnice pre náhľad umiestnenia a uvoľnením ho vložte do sveta.',
                        },
                        clearSelection: {
                            action: 'Vyčistiť označenú oblasť',
                            description:
                                'Podržte Shift a ťahaním označte obdĺžnik; po pustení sa všetky bunky vnútri vymažú.',
                        },
                        undoAction: {
                            action: 'Vrátiť poslednú akciu',
                            description:
                                'Stlačte Ctrl+Z a vrátite najnovšiu úpravu, či už šlo o vymazanie, prepnutie alebo vloženie vzoru.',
                        },
                    },
                },
            },
        },
        modeMenu: {
            title: 'Režimy',
            subtitle: 'Prepínajte správanie a dolaďte, ako sa simulácia vyvíja.',
            closeTitle: 'Zavrieť panel režimov',
            rules: 'Pravidlá',
            weights: 'Váhy',
            parameters: 'Parametre',
            instantUpdate: 'Hodnoty sa aktualizujú okamžite',
            boolean: {
                on: 'Zapnuté',
                off: 'Vypnuté',
            },
            noParameters: 'Tento režim používa predvolené konštanty a momentálne neponúka nastaviteľné parametre.',
            emptyState: 'Vyberte režim zo zoznamu a zobrazia sa jeho detaily.',
            '1dInfo': {
                title: 'Pokročilá Konfigurácia',
                weights: 'Použite tlačidlo Váhy hore pre konfiguráciu váh susedstva, veľkosti a nastavenia prahu.',
            },
            configureWeights: 'Konfigurovať váhy',
            '1dWeightsHelp': 'Konfigurujte váhy susedstva, veľkosť, prah a pravidlá pre 1D celulárny automat.',
            patternSearch: 'Hľadať vzory',
            '1dSearchHelp': 'Automaticky vyhľadávať oscilátory a klzáky v 1D celulárnom automate.',
            patternPresets: 'PREDVOĽBY VZOROV',
            patternPresetsHelp: 'Nastavenia pravidiel s objavenými vzormi. Kliknite pre aplikovanie.',
            patternSingular: 'vzor',
            patternPlural: 'vzory',
        },
        modeRules: {
            title: 'Pravidlá režimu {{mode}}',
            unknownTitle: 'Neznáme pravidlá režimu',
            fallback:
                '<div class="rounded-lg border border-slate-700/70 bg-slate-800/60 p-4 text-slate-200">Pravidlá nie sú k dispozícii.</div>',
            close: 'Zavrieť',
        },

        info: {
            generation: 'Generácia:',
        },
        alerts: {
            invalidConfigurationFile: 'Neplatný súbor konfigurácie.',
        },
        weightEditor: {
            title: 'Editor váh',
            subtitle: 'Kliknite na bunky pre nastavenie ich váh',
            center: 'Stred',
            legend: 'Intenzita váh',
            neighborhoodSize: 'Veľkosť susedstva',
            neighborhoodSizeHelp: 'Susedov na každej strane',
            symmetric: 'Symetrické váhy',
            symmetricHelp: 'Udržiavať ľavé a pravé váhy rovnaké (zrkadlová symetria)',
            birthRules: 'Pravidlá zrodu',
            birthRulesHelp: 'Povolené vážené súčty pre zrod (napr. 2, 3 alebo 2.5-4)',
            survivalRules: 'Pravidlá prežitia',
            survivalRulesHelp: 'Povolené vážené súčty pre prežitie (napr. 2, 4 alebo 2-4.5)',
            currentSum: 'Aktuálny vážený súčet závisí od aktívnych buniek',
            howItWorks: 'Ako to funguje',
            info1: 'Každá susedná bunka prispieva svojou váhou, ak je živá',
            info2: 'Stredová bunka (X) je bunka, ktorá sa vyhodnocuje',
            info3: 'Zrod: mŕtva bunka ožije, ak vážený súčet odpovedá pravidlám (rozsah alebo meno)',
            info4: 'Prežitie: živá bunka prežije, ak vážený súčet odpovedá pravidlám (rozsah alebo meno)',
            info5: 'Veľkosť susedstva určuje počet susedov na každej strane',
            reset: 'Obnoviť predvolené',
            cancel: 'Zrušiť',
            apply: 'Použiť',
        },
        patternSearch: {
            title: 'Hľadač vzorov',
            config: {
                patternWidth: 'Šírka vzoru',
                searchWidth: 'Šírka hľadania',
                maxGen: 'Max. gen.',
                minSeedDensity: 'Min. hustota',
                maxSeedDensity: 'Max. hustota',
                strategy: 'Stratégia',
                searchSmaller: 'Hľadať aj menšie',
                strategies: {
                    random: 'Náhodná',
                    bruteForce: 'Hrubá sila',
                },
                startSearch: 'Spustiť hľadanie',
                stopSearch: 'Zastaviť hľadanie',
                clearResults: 'Vymazať výsledky',
                exportResults: 'Exportovať do JSON',
            },
            filters: {
                title: 'Filtre',
                type: 'Typ',
                all: 'Všetky',
                gliders: 'Klzáky',
                oscillators: 'Oscilátory',
                stillLifes: 'Statický vzor',
                minP: 'Min. P',
                maxP: 'Max. P',
                shift: 'Posun',
            },
            progress: {
                trials: 'Pokusy',
                found: 'Nájdené',
                searching: 'Hľadám...',
            },
            results: {
                noPatternsFound: 'Nenašli sa žiadne vzory zodpovedajúce filtrom.',
                noPatternsYet: 'Zatiaľ sa nenašli žiadne vzory. Upravte šírku alebo generácie a spustite hľadanie.',
                load: 'Načítať',
            },
            info: {
                searchFooter: 'Hľadač kontroluje opakované orezané reťazce bitov naprieč generáciami. Klzáky sú detegované priestorovým posunom medzi opakovaniami.',
            }
        },
        modes: {
            halfLife: {
                label: 'HalfLife',
            },
        },
    },
};


Object.entries(modeTranslations).forEach(([lang, modesMap]) => {
    if (!translations[lang]) {
        translations[lang] = {};
    }
    translations[lang].modes = {
        ...(translations[lang].modes || {}),
        ...modesMap,
    };
});

const SUPPORTED_LANGUAGES = Object.keys(translations);
const FALLBACK_LANGUAGE = 'en';
const LANGUAGE_STORAGE_KEY = 'fuzzyLife.language';

const LanguageContext = createContext({
    language: FALLBACK_LANGUAGE,
    setLanguage: () => { },
    translate: (key) => key,
});

const resolveValue = (collection, key) => {
    return key.split('.').reduce((accumulator, segment) => {
        if (accumulator && Object.prototype.hasOwnProperty.call(accumulator, segment)) {
            return accumulator[segment];
        }
        return undefined;
    }, collection);
};

const formatTemplate = (value, replacements = {}) => {
    if (typeof value !== 'string') {
        return value;
    }
    return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) =>
        Object.prototype.hasOwnProperty.call(replacements, token) ? String(replacements[token]) : ''
    );
};

const translateValue = (language, key, replacements) => {
    const primary = resolveValue(translations[language], key);
    if (primary !== undefined) {
        return typeof primary === 'string' ? formatTemplate(primary, replacements) : primary;
    }
    if (language !== FALLBACK_LANGUAGE) {
        const fallback = resolveValue(translations[FALLBACK_LANGUAGE], key);
        if (fallback !== undefined) {
            return typeof fallback === 'string' ? formatTemplate(fallback, replacements) : fallback;
        }
    }
    return formatTemplate(key, replacements);
};

const getInitialLanguage = () => {
    if (typeof window === 'undefined') {
        return FALLBACK_LANGUAGE;
    }
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
        return stored;
    }
    const browserLanguage = window.navigator.language || '';
    const normalized = browserLanguage.toLowerCase();
    const detected = SUPPORTED_LANGUAGES.find((code) => normalized.startsWith(code));
    return detected || FALLBACK_LANGUAGE;
};

export const LanguageProvider = ({ children }) => {
    const [languageState, setLanguageState] = useState(getInitialLanguage);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(LANGUAGE_STORAGE_KEY, languageState);
            if (typeof document !== 'undefined' && document.documentElement) {
                document.documentElement.setAttribute('lang', languageState);
            }
        }
    }, [languageState]);

    const setLanguage = useCallback(
        (nextLanguage) => {
            if (SUPPORTED_LANGUAGES.includes(nextLanguage)) {
                setLanguageState(nextLanguage);
            }
        },
        [setLanguageState]
    );

    const value = useMemo(
        () => ({
            language: languageState,
            setLanguage,
            translate: (key, replacements) => translateValue(languageState, key, replacements),
        }),
        [languageState, setLanguage]
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

export const useTranslation = () => {
    const context = useLanguage();
    return {
        t: context.translate,
        language: context.language,
        setLanguage: context.setLanguage,
    };
};

export { SUPPORTED_LANGUAGES };
